<script setup lang="ts">
import { computed, toRefs } from "vue";
import {
	DashboardLayout,
	DashboardLayoutItem,
} from "@/modules/dashboards/pages/DashboardTypes";
import { useBaseStore } from "@/stores/base";
import DashboardCardDO = App.Data.DashboardCardDO;

const isContextMenuVisible = defineModel<boolean>();
const userHasAccessToExplorer = computed(() =>
	useBaseStore().currentUser?.derivedPermissions.includes("can_manage_explorer")
);

const props = withDefaults(
	defineProps<{
		layout: DashboardLayout;
		clickedCard: DashboardLayoutItem;
		clickedCardFull: DashboardCardDO | null;
		position?: { x: number; y: number };
	}>(),
	{
		position: () => ({ x: 0, y: 0 }),
	}
);

const emit = defineEmits<{
	delete: [card: DashboardLayoutItem];
	edit: [card: DashboardLayoutItem];
	viewInExplorer: [card: DashboardLayoutItem];
	editInExplorer: [card: DashboardLayoutItem];
	downloadCard: [card: DashboardLayoutItem];
}>();

const { layout, position, clickedCard, clickedCardFull } = toRefs(props);

const overlapsOtherCard = (
	layout: DashboardLayout,
	clickedCard: DashboardLayoutItem,
	width: number
): boolean => {
	if (!clickedCard) {
		return false;
	}
	const newX = Math.min(clickedCard.x, 12 - width); // GridLayout pushes the x value back if it exceeds the grid
	return layout.some(
		(card) =>
			card.i !== clickedCard.i &&
			card.x < newX + width &&
			card.x + card.w > newX &&
			card.y < clickedCard.y + clickedCard.h &&
			card.y + card.h > clickedCard.y
	);
};
const setWidth = (width: number) => {
	const clicked = clickedCard.value;
	if (!clicked) {
		return;
	}
	if (overlapsOtherCard(layout.value, clicked, width)) {
		return;
	}
	// Clamp x ourselves, otherwise GridLayout resets an out-of-bounds card to x = 0
	clicked.x = Math.min(clicked.x, 12 - width);
	clicked.w = width;
};

const canDownloadCard = computed(
	() =>
		clickedCardFull.value?.type === "table" ||
		clickedCardFull.value?.type === "graph"
);

const canOpenInExplorer = computed(
	() =>
		!!clickedCardFull.value?.stored_document?.active_document_uuid &&
		!!userHasAccessToExplorer.value
);

const sections = computed(() => [
	{
		title: "Width",
		items: [
			{
				title: "1/3",
				action: () => setWidth(4),
				disabled:
					overlapsOtherCard(layout.value, clickedCard.value, 4) ||
					clickedCard.value.w == 4,
			},
			{
				title: "1/2",
				action: () => setWidth(6),
				disabled:
					overlapsOtherCard(layout.value, clickedCard.value, 6) ||
					clickedCard.value.w == 6,
			},
			{
				title: "Full width",
				action: () => setWidth(12),
				disabled:
					overlapsOtherCard(layout.value, clickedCard.value, 12) ||
					clickedCard.value.w == 12,
			},
		],
	},
	{
		title: "Card",
		items: [
			{
				title: "Delete",
				action: () => emit("delete", clickedCard.value),
				disabled: false,
			},
			{
				title: "Edit",
				action: () => emit("edit", clickedCard.value),
				disabled: false,
			},
			{
				title: "View in Explorer",
				action: () => emit("viewInExplorer", clickedCard.value),
				disabled: !canOpenInExplorer.value,
			},
			{
				title: "Edit in Explorer",
				action: () => emit("editInExplorer", clickedCard.value),
				disabled: !canOpenInExplorer.value,
			},
			...(canDownloadCard.value
				? [
						{
							title: "Download",
							action: () => emit("downloadCard", clickedCard.value),
							disabled: false,
						},
				  ]
				: []),
		],
	},
]);
</script>

<template>
	<v-menu
		v-model="isContextMenuVisible"
		:close-on-content-click="true"
		:nudge-width="200"
		location-strategy="connected"
		:target="[position.x, position.y]"
		absolute
	>
		<v-card class="d-flex flex-row">
			<template v-for="(section, index) in sections" :key="section.title">
				<v-divider v-if="index > 0" vertical />
				<v-list density="compact">
					<v-list-subheader class="font-weight-bold">
						{{ section.title }}
					</v-list-subheader>
					<v-list-item
						v-for="item in section.items"
						:key="item.title"
						@click="item.action"
						:disabled="item.disabled"
						class="font-weight-regular text-caption"
					>
						{{ item.title }}
					</v-list-item>
				</v-list>
			</template>
		</v-card>
	</v-menu>
</template>

<style scoped lang="scss"></style>
